/**
 * FikoYT CAM — CATIA Prismatic Machining tarzı tip modeli (SIFIRDAN, eski 2D CAM ile ilgisi yok).
 * Yapı: Manufacturing Program → Part Operation(lar) → Machining Operation(lar) + Tool Change.
 * Her operasyon CATIA 5-sekme verisini taşır: Geometry · Strategy · Tool · Feeds&Speeds · Macros.
 */

// ───────────────────── Takım (NC Tool) ─────────────────────
export type TakimTip =
  | 'faceMill' | 'endMill' | 'ballMill' | 'bullMill' | 'conicalMill' | 'tslotMill' | 'threadMill'
  | 'centerDrill' | 'spotDrill' | 'drill' | 'countersink' | 'counterbore' | 'reamer'
  | 'boringBar' | 'tap' | 'multiDrill' | 'chamferMill';

export interface ITakim {
  id: string;
  ad: string;
  tip: TakimTip;
  cap: number;            // takım çapı / 1. kademe çapı (D, mm)
  kose?: number;          // köşe yarıçapı (bull/ball/conical Rc / t-slot alt Rc)
  ucAci?: number;         // matkap/havşa uç açısı / 1. açı (a / a1, °)
  boy: number;            // kesici boyu (lc)
  toplamBoy?: number;     // toplam uzunluk (L, gauge)
  agizSayisi?: number;    // flüt/ağız sayısı
  hatve?: number;         // kılavuz/diş hatvesi (tap/thread mill, mm)
  numara?: number;        // takım numarası (T-no, post için)
  // — Kademeli / özel takımlar (CATIA NC Tools) —
  cap2?: number;          // 2. kademe çapı (Dc — multi-drill / boring-chamfer)
  cap3?: number;          // 3. kademe çapı (Dc2 — multi-drill)
  girisCap?: number;      // giriş/alt çapı (d — conical alt / countersink / two-sides / reamer)
  kademeBoy1?: number;    // 1. kademe boyu (l1)
  kademeBoy2?: number;    // 2. kademe boyu (l2)
  ucAci2?: number;        // 2. açı (a2 — center taper / multi top / two-sides üst pah, °)
  ucAci3?: number;        // 3. açı (a3 — multi taper, °)
  kose2?: number;         // üst kesim yarıçapı (Rc2 — t-slot üst)
  sapCap?: number;        // gövde/sap çapı (Db)
}

// ───────────────────── Operasyon türleri (CATIA) ─────────────────────
export type FrezeTip = 'facing' | 'pocketing' | 'contouring' | 'curveFollow' | 'pointToPoint' | 'circularMill';
export type AxialTip =
  | 'spotDrill' | 'drilling' | 'deepHole' | 'breakChips' | 'tapping' | 'reverseTap' | 'threadNoTap'
  | 'boring' | 'boringChamfer' | 'boringStop' | 'reaming' | 'counterBore' | 'counterSink'
  | 'chamfer2' | 'backBore' | 'tslotting' | 'threadMilling';
export type OperasyonTip = FrezeTip | AxialTip;

export const FREZE_TIPLER: FrezeTip[] = ['facing', 'pocketing', 'contouring', 'curveFollow', 'pointToPoint', 'circularMill'];
export const AXIAL_TIPLER: AxialTip[] = ['spotDrill', 'drilling', 'deepHole', 'breakChips', 'tapping', 'reverseTap', 'threadNoTap', 'boring', 'boringChamfer', 'boringStop', 'reaming', 'counterBore', 'counterSink', 'chamfer2', 'backBore', 'tslotting', 'threadMilling'];

// ───────────────────── Geometry sekmesi ─────────────────────
export interface ICamGeometri {
  konturlar?: { x: number; y: number }[][];   // 2D kapalı/açık kontur(lar) (part düzleminde)
  adalar?: { x: number; y: number }[][];      // cep içi ada konturları (işlenmez, etrafından dolaşılır)
  noktalar?: { x: number; y: number; z: number; aktif?: boolean; baslangic?: boolean }[];   // axial op delik noktaları (aktif=false → atla, baslangic → sıralama başı)
  cap?: number;             // axial özel ops hedef çapı (thread nominal / t-slot genişliği / counterbore)
  ustKosul?: 'hard' | 'soft';   // contour üst koşul (soft = üstten serbest)
  altKosul?: 'hard' | 'soft';   // contour alt koşul (soft = breakthrough kadar geç)
  ustZ?: number;            // işleme üst kotu (stock top / face)
  altZ?: number;            // işleme alt kotu (derinlik tabanı)
  ustZRef?: number;         // 3D'den seçilmiş üst düzlem referans yüzü (faceId) — associatif (yüz değişince Z takip eder)
  altZRef?: number;         // 3D'den seçilmiş alt/taban düzlem referans yüzü (faceId) — associatif
  derinlikRef?: number;     // 3D'den seçilmiş taban yüzü (faceId) — derinlik = ustZ − yüzZ (associatif)
  yuzIdx?: number[];        // seçili part yüz indeksleri (3D'den)
  derinlik?: number;        // axial delik derinliği
  // CATIA Offsets
  ofsetUst?: number;        // Offset on Top
  ofsetKontur?: number;     // Offset on Contour (yan pay)
  ofsetTaban?: number;      // Offset on Bottom
  ofsetCheck?: number;      // Offset on Check (fikstür payı)
  baslangic?: { x: number; y: number };   // start point
  bitis?: { x: number; y: number };        // end point
  // CATIA Open Pocket / Hard-Soft sınır
  acikKenarlar?: number[];  // Open Pocket: soft (açık) dış kontur segment indeksleri — takım bu kenarda dışarı taşar (hard kenar: r içe)
  tabanAcik?: boolean;      // Soft bottom: takım tabanı altZ'yi geçer (delikli/açık taban), kapalı = kör cep (hard bottom)
  breakthrough?: number;    // soft bottom geçiş payı (mm) — altZ altına inilen mesafe (default takım yarıçapı)
}

// ───────────────────── Strategy sekmesi (CATIA tam set) ─────────────────────
export type FrezeYon = 'climb' | 'conventional';            // Climb(eş) / Conventional(zıt)
export type ToolPathStyle = 'helicalIn' | 'backForth' | 'oneWay';   // CATIA Tool Path Style
export type KoseTip = 'circular' | 'angular' | 'optimized' | 'forcedCircular';   // Type of Contour (corners)
export type RadialMod = 'maxDist' | 'toolRatio' | 'distPaths' | 'pctTool';        // Radial Mode
export type AxialMod = 'maxDepth' | 'numLevels' | 'numLevelsNoTop';               // Axial Mode
export type FinisMod = 'yok' | 'tabanda';                                          // Finishing Mode
export type EndOfPath = 'inside' | 'outside';
export type TemizlemeStrateji = 'raster' | 'offsetIn' | 'offsetOut' | 'spiral' | 'trochoidal';
export type AxialDongu = 'standart' | 'peck' | 'breakChip' | 'dwell';
export type DwellMod = 'kapali' | 'devir' | 'sure';

export interface ICamStrateji {
  // — Machining —
  toolPathStyle?: ToolPathStyle;   // helicalIn / backForth / oneWay
  strateji?: TemizlemeStrateji;    // (eski/uyumluluk) raster/offset
  yon?: FrezeYon;                  // Direction of Cut
  taraf?: 'ic' | 'dis' | 'uzeri';  // CATIA Tool Side: takım profilin İÇİNDEN / DIŞINDAN / ÜZERİNDEN geçer (contour)
  koseTip?: KoseTip;               // Type of Contour (köşe işleme: circular/angular/optimized/forcedCircular)
  koseR?: number;                  // köşe yuvarlama yarıçapı (circular/forcedCircular, mm) — yoksa takım yarıçapı oranı
  makineToleransi?: number;        // machining tolerance
  kompanzasyon?: number;           // cutter compensation no (G41/G42 D-no)
  kompanzasyonAktif?: boolean;     // cutter compensation G41/G42 post'ta etkin mi
  // — Radial (stepover) —
  radialMod?: RadialMod;
  yanalHatve?: number;             // maxDist / distPaths değeri (mm)
  yanalHatveYuzde?: number;        // pctTool / toolRatio değeri (%)
  endOfPath?: EndOfPath;           // inside/outside
  overhang?: number;               // % tool dia
  yanYaklasma?: number;            // tool side approach clearance
  // — Axial (stepdown) —
  axialMod?: AxialMod;
  eksenelHatve?: number;           // max depth of cut (mm)
  seviyeSayisi?: number;           // number of levels
  // — Finishing —
  finisMod?: FinisMod;
  finisPayi?: number;              // radial stock to leave
  tabanPayi?: number;              // bottom finish thickness
  // — Facing özel —
  rasterAci?: number;              // raster açısı (°)
  // — Axial (delme) —
  dongu?: AxialDongu;
  peckDerinlik?: number;           // her peck adımı
  geriCekme?: number;              // peck geri çekme / jump
  dwellMod?: DwellMod;
  dwellDeger?: number;             // devir sayısı veya saniye
  breakthrough?: number;           // through-hole: delik dibini bu kadar geç (delip çıkma payı, mm)
  decrement?: number;              // deep hole: her peck'te derinlik azaltma oranı (0-1)
  decrementLimit?: number;         // deep hole: minimum peck derinliği (mm)
  noktaSirala?: 'sira' | 'enYakin';   // delik sıralama (girdiği sıra / en yakın komşu)
  // — Circular Milling —
  daireMod?: 'standard' | 'helical';   // standard = kat kat düz daire, helical = helisel iniş
  daireYolSayisi?: number;         // radyal pas sayısı (büyük delik)
  // — Finishing ek —
  finisTam?: boolean;              // yan + taban birlikte finiş pası
  avoidScallops?: boolean;         // son katta yanal hatveyi düşür (kalıntı tepe önle)
  // — Tabs / Bridges (parça tutturma — son katta köprü bırak) —
  tabAktif?: boolean;              // köprü (tab) bırak
  tabAdet?: number;               // kontur çevresine eşit dağıtılan köprü sayısı
  tabGenislik?: number;           // her köprünün eni (mm)
  tabYukseklik?: number;          // köprü kalınlığı — son kat tabanından bu kadar yukarı kalır (mm)
  // — Trochoidal (dar kanal/slot için döngüsel yüksek-verim yol) —
  trochoidalAktif?: boolean;      // pocket/slot'ta trochoidal yol kullan
  trochoidalAdim?: number;        // her döngü ilerleme adımı (mm)
  trochoidalGenislik?: number;    // döngü genişliği (mm, takım çapının yüzdesi gibi)
}

// ───────────────────── Feeds & Speeds sekmesi ─────────────────────
export interface ICamHizlar {
  devir: number;             // spindle RPM
  kesimHiz?: number;         // surface speed Vc (m/min) — opsiyonel hesap
  ilerleme: number;          // feed (mm/min) — cutting
  dalmaIlerleme?: number;    // plunge feed
  yaklasmaIlerleme?: number; // approach feed
  geriCekmeIlerleme?: number;// retract feed (rapid varsayılan)
  disBasinaIlerleme?: number;// fz (mm/tooth) — opsiyonel
  finisIlerleme?: number;    // finishing pası ayrı feed (mm/min)
}

// ───────────────────── Macros sekmesi (CATIA: birden fazla ayrı makro) ─────────────────────
export type GirisTip = 'dikine' | 'rampa' | 'helis' | 'yay' | 'on';   // plunge/ramp/helix/arc/pre-drilled
/** CATIA Macro Management — her makro ayrı tanımlanır (Approach / Retract / Linking). */
export type MakroTip =
  | 'approach'        // Yaklaşma (malzemeye giriş)
  | 'retract'         // Geri çekme (malzemeden çıkış)
  | 'linkApproach'    // Bağlantı yaklaşma (pas/bölge arası giriş)
  | 'linkRetract'     // Bağlantı geri çekme
  | 'returnLevel'     // Seviye içi dönüş (Return in a Level)
  | 'returnBetween'   // Seviyeler arası dönüş (Return Between Levels)
  | 'clearance';      // Güvenlik düzlemi (Clearance)

export const MAKRO_AD: Record<MakroTip, string> = {
  approach: 'Yaklaşma', retract: 'Geri Çekme', linkApproach: 'Bağlantı Yaklaşma',
  linkRetract: 'Bağlantı Geri Çekme', returnLevel: 'Seviye İçi Dönüş',
  returnBetween: 'Seviyeler Arası Dönüş', clearance: 'Güvenlik Düzlemi',
};

// CATIA "Build by user" makro hareket primitifleri (özel makro adımları).
export type MakroAdimTip =
  | 'eksenel'    // tool axis boyunca eksenel (dik) iniş/çıkış (mesafe)
  | 'teget'      // yola teğet yatay hareket (mesafe)
  | 'rampa'      // açılı rampa (aci + mesafe)
  | 'helis'      // helisel iniş (yaricap + aci, eksenel mesafe)
  | 'duzleme';   // mutlak Z düzlemine kadar eksenel (hedefZ)

export const MAKRO_ADIM_AD: Record<MakroAdimTip, string> = {
  eksenel: 'Eksenel', teget: 'Teğet', rampa: 'Rampa', helis: 'Helis', duzleme: 'Düzleme Kadar',
};

/** Özel makro tek hareket adımı (sıralı dizi → giriş/çıkış yolu). */
export interface IMakroAdim {
  tip: MakroAdimTip;
  mesafe?: number;   // eksenel/teğet/rampa yatay-ya da-eksenel uzaklık (mm)
  aci?: number;      // rampa/helis açısı (°)
  yaricap?: number;  // helis yarıçapı (mm)
  hedefZ?: number;   // 'duzleme': gidilecek mutlak Z (mm)
  hedefZRef?: number;  // 'duzleme': 3D'den seçilmiş referans yüz (faceId) — associatif (yüz değişince Z takip eder)
}

/** Tek bir makro tanımı (CATIA: bir hareket dizisi/giriş-çıkış stratejisi). */
export interface ICamMakro {
  tip: MakroTip;
  aktif: boolean;
  mod?: 'hazir' | 'custom';  // hazir = hareketTip preset; custom = adimlar dizisi (CATIA Build by user)
  hareketTip?: GirisTip;     // dikine/rampa/helis/yay/ön-delik (approach/retract için)
  adimlar?: IMakroAdim[];    // mod='custom' → sıralı hareket primitifleri
  aci?: number;              // rampa/helis açısı (°)
  yaricap?: number;          // helis/yay yarıçapı (mm)
  mesafe?: number;           // teğetsel/eksenel uzaklık (mm)
  guvenlikZ?: number;        // clearance/approach: güvenlik düzlemi Z
  yaklasmaZ?: number;        // feed-plane (rapid→feed geçiş kotu)
  jumpYuksek?: number;       // linking: bölgeler/delikler arası sıçrama kotu
  guvenlikZRef?: number;     // 3D'den seçilmiş güvenlik düzlemi referans yüzü (faceId) — associatif
  yaklasmaZRef?: number;     // 3D'den seçilmiş yaklaşma düzlemi referans yüzü (faceId) — associatif
}

/** ICamMakro listesinden istenen tipi bulur. */
export function makroBul(op: { makrolar?: ICamMakro[] }, tip: MakroTip): ICamMakro | undefined {
  return op.makrolar?.find(m => m.tip === tip);
}
/** Etkin güvenlik Z (clearance → approach sırasıyla). */
export function makroGuvZ(op: { makrolar?: ICamMakro[] }, vars: number): number {
  return makroBul(op, 'clearance')?.guvenlikZ ?? makroBul(op, 'approach')?.guvenlikZ ?? vars;
}

// ───────────────────── Tool Axis (takım yönü — CATIA 3+2 / 5-axis) ─────────────────────
/** Takım ekseni kaynağı: z=part Z (varsayılan), yuzey=seçilen B-Rep yüz normali, plane=XY/XZ/YZ, custom=elle vektör. */
export type TakimYonKaynak = 'z' | 'yuzey' | 'plane' | 'custom';
/** Takım ekseni tanımı. yon = takımın baktığı doğrultu (malzemeye -yon yönünde iner). Varsayılan {0,0,1}=Z. */
export interface ITakimYonu {
  yon: { x: number; y: number; z: number };   // normalize tool axis (world)
  kaynak?: TakimYonKaynak;
  yuzId?: number;                              // kaynak=yuzey ise B-Rep faceId
  origin?: { x: number; y: number; z: number }; // tool-axis düzlem orijini (3+2 işlem düzlemi referansı)
}

// ───────────────────── Machining Operation ─────────────────────
export interface ICamOperasyon {
  id: string;
  ad: string;
  tip: OperasyonTip;
  yorum?: string;
  takimId?: string;
  geometri: ICamGeometri;
  strateji: ICamStrateji;
  hizlar: ICamHizlar;
  makrolar: ICamMakro[];     // CATIA: birden fazla ayrı makro (approach/retract/linking)
  takimYonu?: ITakimYonu;    // CATIA Tool Axis (3+2 / 5-axis) — yoksa part Z varsayılır
  hesaplandi?: boolean;      // toolpath güncel mi
  bayat?: boolean;           // CATIA associativity: model/parametre değişti, toolpath güncel değil (⟳ — Hesapla ile güncellenir)
  bastirilmis?: boolean;     // suppress
  node?: any;                // ürün ağacı node'u (FikoCamAktivite)
  yolNode?: any;             // operasyon altındaki toolpath node'u (FikoTakimYoluv1 — CATIA)
}

// ───────────────────── Part Operation ─────────────────────
export interface IPartOperasyon {
  id: string;
  ad: string;
  makineTip: '3axis' | '3axisHoriz' | '5axis' | 'lathe';
  eksenOrigin: { x: number; y: number; z: number };   // machining axis system origin (G54 sıfır noktası, world)
  // CATIA Machining Axis System: NC kod bu eksenlere göre üretilir (yoksa world X/Y/Z varsayılır).
  eksenX?: { x: number; y: number; z: number };   // makine X yönü (world)
  eksenY?: { x: number; y: number; z: number };   // makine Y yönü (world)
  eksenZ?: { x: number; y: number; z: number };   // makine Z yönü (world, spindle)
  stok?: { en: number; boy: number; yuk: number; x: number; y: number; z: number };   // rough stock kutusu
  workOffset?: string;       // iş koordinat sistemi (G54-G59, default G54)
  malzeme?: string;          // parça malzemesi (MALZEME_KESIM anahtarı) — Feeds&Speeds otomatik hesabı bunu kullanır
  guvenlikZ: number;         // safety plane
  guvenlikZRef?: number;     // 3D'den seçilmiş güvenlik düzlemi referans yüzü (faceId) — associatif; guvenlikZOfset üstüne eklenir
  guvenlikZOfset?: number;   // referanslı güvenlik düzlemine ek offset (mm, default 10 — takım yüzün bu kadar üstünde gezer)
  operasyonlar: ICamOperasyon[];
  node?: any;
}

// ───────────────────── Toolpath (hesaplanan yol) ─────────────────────
export type HareketTip = 'rapid' | 'feed' | 'plunge' | 'retract' | 'approach';
/** arc: dairesel interpolasyon (post G02/G03). cx/cy = yay merkezi (mutlak), cw = saat yönü.
 * i/j/k: takım ekseni vektörü (tool axis, 3+2/5-axis NC için — yoksa part Z varsayılır). */
export interface ICamHareket { tip: HareketTip; x: number; y: number; z: number; f?: number; arc?: { cx: number; cy: number; cw: boolean }; i?: number; j?: number; k?: number; mk?: 'app' | 'ret'; }
export interface ICamToolpath {
  opId: string;
  hareketler: ICamHareket[];
  sure?: number;             // tahmini süre (dk)
  uzunluk?: number;          // toplam yol (mm)
  uyarilar?: string[];       // güvenlik/doğrulama uyarıları (takım eksik vb — UI gösterir)
}

// ───────────────────── Yardımcı: operasyon görünen adları (CATIA TR) ─────────────────────
export const OPERASYON_AD: Record<OperasyonTip, string> = {
  facing: 'Yüzey Tarama (Facing)', pocketing: 'Cep (Pocketing)', contouring: 'Profil (Contouring)',
  curveFollow: 'Eğri Takip (Curve Following)', pointToPoint: 'Nokta-Nokta', circularMill: 'Dairesel Freze',
  spotDrill: 'Punta (Spot Drill)', drilling: 'Delme (Drilling)', deepHole: 'Derin Delik', breakChips: 'Talaş Kırma',
  tapping: 'Kılavuz (Tapping)', reverseTap: 'Ters Kılavuz', threadNoTap: 'Diş (Tapsız)',
  boring: 'Bara (Boring)', boringChamfer: 'Bara + Pah', boringStop: 'Bara (Mil Durdur)', reaming: 'Rayba (Reaming)',
  counterBore: 'Havşa Düz (Counterbore)', counterSink: 'Havşa Konik (Countersink)', chamfer2: 'Çift Yön Pah',
  backBore: 'Ters Bara', tslotting: 'T-Kanal', threadMilling: 'Diş Freze',
};

export const TAKIM_AD: Record<TakimTip, string> = {
  faceMill: 'Tarama Kafası', endMill: 'Parmak Freze', ballMill: 'Küresel Freze', bullMill: 'Toroidal Freze',
  conicalMill: 'Konik Freze', tslotMill: 'T-Kanal Freze', threadMill: 'Diş Freze',
  centerDrill: 'Punta Matkap', spotDrill: 'Spot Matkap', drill: 'Matkap', countersink: 'Havşa Matkap',
  counterbore: 'Havşa Düz', reamer: 'Rayba', boringBar: 'Bara Çubuğu', tap: 'Kılavuz', multiDrill: 'Çok-Çaplı Matkap', chamferMill: 'Pah Freze',
};
